/**
 * Adds the task types of the published courses (Create Course > Task Type) to
 * the Learning Zone sidebar: a course in English with task type "Revision"
 * adds a "Revision" row under English, after its existing rows. Same for
 * Computer Apps, Stories and Fun with Art. Every existing row stays as it is;
 * a task type already shown as a row there (English > Scenes) isn't repeated.
 */

// Sidebar group id -> the admin course category it lists.
const GROUP_CATEGORIES = {
  english: 'English',
  'computer-apps': 'Computer Apps',
  stories: 'Stories',
  'art-therapy': 'Art',
};

const TASK_TYPE_ICONS = {
  story: '📖',
  scene: '🎬',
  revision: '🔁',
  poem: '📝',
  buddy_system: '🤝',
  art: '🎨',
};

const lower = (value) => String(value || '').trim().toLowerCase();

/**
 * Returns the sidebar tree with the task type rows added, plus `views`: view
 * id -> what that row shows ({ title, category, taskType, chapters }).
 * `categories` comes from GET .../courses/learning-zone.
 */
export function addCourseTaskTypeRows(tree, categories = []) {
  const views = {};
  const byCategory = new Map(categories.map((category) => [lower(category.name), category]));

  const withRows = tree.map((group) => {
    const category = byCategory.get(lower(GROUP_CATEGORIES[group.id]));
    if (!category) return group;

    // Fun with Art has no rows of its own; its task type rows are its first.
    const existing = group.children || [];
    const shown = new Set(existing.map((child) => lower(child.label)));
    // Stories uses small rows; new rows match their siblings.
    const compact = existing.length > 0 && existing.every((child) => child.compact);
    const rows = (category.taskTypes || [])
      .filter((taskType) => !shown.has(lower(taskType.label)))
      .map((taskType) => {
        const view = `course:${group.id}:${taskType.taskType}`;
        views[view] = {
          title: taskType.label,
          category: category.name,
          taskType: taskType.taskType,
          chapters: taskType.chapters || [],
        };
        return {
          id: `${group.id}--${taskType.taskType}`,
          label: taskType.label,
          icon: TASK_TYPE_ICONS[taskType.taskType] || '📚',
          view,
          ...(compact ? { compact: true } : {}),
        };
      });

    return rows.length ? { ...group, children: [...existing, ...rows] } : group;
  });

  return { tree: withRows, views };
}
